import { useEffect, useState, useCallback } from 'react';
import { realtimeService } from '../services/realtimeService';
import { useAuth } from '../contexts/AuthContext';

/**
 * Custom hook for subscribing to multiple entities at once
 * Useful for dashboard views that need multiple data sources
 * 
 * @param {Array} entityConfigs - Array of {entity, initialData, transformFn} objects
 * @returns {Object} { entityData, isConnected, refreshEntity }
 */
export const useRealtimeMultiEntity = (entityConfigs = []) => {
  const { profile } = useAuth();
  const [entityData, setEntityData] = useState({});
  const [isConnected, setIsConnected] = useState(false);

  // Initialize entity data
  useEffect(() => {
    const initialData = {};
    entityConfigs?.forEach(({ entity, initialData: data }) => {
      initialData[entity] = data || [];
    });
    setEntityData(initialData);
  }, []);

  // Create handlers for each entity
  const createHandlers = useCallback((entity, transformFn) => {
    return {
      onInsert: (newRecord) => {
        const transformed = transformFn ? transformFn(newRecord) : newRecord;
        setEntityData(prev => ({
          ...prev,
          [entity]: [transformed, ...(prev?.[entity] || [])]
        }));
      },
      onUpdate: (updatedRecord) => {
        const transformed = transformFn ? transformFn(updatedRecord) : updatedRecord;
        setEntityData(prev => ({
          ...prev,
          [entity]: (prev?.[entity] || [])?.map(item =>
            item?.id === transformed?.id ? { ...item, ...transformed } : item
          )
        }));
      },
      onDelete: (deletedRecord) => {
        setEntityData(prev => ({
          ...prev,
          [entity]: (prev?.[entity] || [])?.filter(item => item?.id !== deletedRecord?.id)
        }));
      }
    };
  }, []);

  // Subscribe to all entities
  useEffect(() => {
    if (!profile?.tenant_id || entityConfigs?.length === 0) {
      console.warn('[useRealtimeMultiEntity] Cannot subscribe: missing tenant_id or no entities');
      return;
    }

    console.log(`[useRealtimeMultiEntity] Subscribing to ${entityConfigs?.length} entities`);
    setIsConnected(true);

    const subscriptions = entityConfigs?.map(({ entity, transformFn }) => {
      const handlers = createHandlers(entity, transformFn);
      return realtimeService?.subscribeToEntity(entity, profile?.tenant_id, handlers);
    });

    return () => {
      console.log('[useRealtimeMultiEntity] Unsubscribing from all entities');
      setIsConnected(false);
      subscriptions?.forEach(unsubscribe => unsubscribe());
    };
  }, [profile?.tenant_id, entityConfigs, createHandlers]);

  // Refresh specific entity data
  const refreshEntity = useCallback((entity, newData) => {
    setEntityData(prev => ({
      ...prev,
      [entity]: newData
    }));
  }, []);

  return {
    entityData,
    isConnected,
    refreshEntity
  };
};

export default useRealtimeMultiEntity;
